import { useEffect } from 'react';
import { App } from '@capacitor/app';
import { useAuth } from '@/contexts/AuthContext';
import { useNotificationsBadge } from '@/contexts/NotificationsBadgeContext';
import { isNativePlatform } from '@/lib/platform';
import { error } from '@/utils/logger';

const WEB_POLL_INTERVAL_MS = 60_000;

/**
 * Keeps the notifications badge fresh: web polls on visibility + interval;
 * native refreshes on App resume.
 */
export function useNotificationPolling(): void {
  const { isAuthenticated } = useAuth();
  const { refreshUnreadCount } = useNotificationsBadge();

  useEffect(() => {
    if (!isAuthenticated) return;

    if (isNativePlatform()) {
      const listenerPromise = App.addListener('appStateChange', ({ isActive }) => {
        if (isActive) {
          void refreshUnreadCount();
        }
      });

      return () => {
        void listenerPromise.then((handle) => handle.remove()).catch((err) => {
          error('[useNotificationPolling] Failed to remove appStateChange listener', err);
        });
      };
    }

    const poll = () => {
      if (document.visibilityState === 'visible') {
        void refreshUnreadCount();
      }
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        void refreshUnreadCount();
      }
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    const intervalId = window.setInterval(poll, WEB_POLL_INTERVAL_MS);
    poll();

    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.clearInterval(intervalId);
    };
  }, [isAuthenticated, refreshUnreadCount]);
}
